import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import type { GameDeckMeta, GameIntensityLevel, GameSession } from "../../../types";

const CATEGORY_EMOJI: Record<string, string> = {
  questions: "\u{1F4AC}",
  truth_or_dare: "\u{1F608}",
  spicy: "\u{1F525}",
};

// Lightweight haptic nicety, feature-detected - most desktop browsers and
// some mobile browsers don't implement navigator.vibrate at all.
function buzz(ms: number): void {
  if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
    navigator.vibrate(ms);
  }
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1450;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
`;

// Solid, not the shared .glass translucency - same reasoning as
// AppDialog.tsx/TogetherChatInput.tsx: a blurred translucent panel over an
// arbitrary background (here, a dark scrim over whatever screen was open)
// washes out grey instead of reading as a clean white/dark card.
const Panel = styled.div`
  background: var(--surface-opaque);
  border: 1px solid var(--surface-border);
  box-shadow: var(--shadow), inset 0 1px 0 rgba(255, 255, 255, 0.5);
  border-radius: var(--radius);
  width: 100%;
  max-width: 420px;
  max-height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

// A fuller rotateY flip (perspective on the wrapper, 90deg->0 on the
// content) for a more literal "card flip" read than a plain fade/scale.
const cardFlip = keyframes`
  0% { opacity: 0; transform: rotateY(-90deg) scale(0.92); }
  60% { opacity: 1; }
  100% { opacity: 1; transform: rotateY(0) scale(1); }
`;

const CardBody = styled.div`
  perspective: 800px;
  > * {
    animation: ${cardFlip} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform-origin: center;
  }
`;

const TurnPill = styled.span<{ $mine: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  ${(p) =>
    p.$mine
      ? `background: var(--pink-500); color: #fff;`
      : `background: transparent; color: var(--muted); border: 1px solid var(--surface-border);`}
`;

export function GameStage({
  session,
  deckMeta,
  intensityMeta,
  lastAnswer,
  leaving,
  drawing,
  skipping,
  onDraw,
  onSkip,
  onAnswer,
  onLeave,
  onPlayAgain,
  onChooseAnotherDeck,
}: {
  session: GameSession;
  deckMeta: GameDeckMeta | null;
  intensityMeta: GameIntensityLevel | null;
  lastAnswer: { text: string; senderName: string; mine: boolean } | null;
  leaving: boolean;
  drawing: boolean;
  skipping: boolean;
  onDraw: () => void;
  onSkip: () => void;
  onAnswer: (text: string) => void;
  onLeave: () => void;
  onPlayAgain: () => void;
  onChooseAnotherDeck: () => void;
}) {
  const [answerText, setAnswerText] = useState("");
  const [answering, setAnswering] = useState(false);
  const [playingAgain, setPlayingAgain] = useState(false);
  const [choosingDeck, setChoosingDeck] = useState(false);

  const card = session.currentCard;
  const cardKey = card ? `${card.cardId}:${card.resolution}` : "empty";
  const deckEmoji = session.mode === "mixed" ? "\u{1F3B4}" : (deckMeta?.emoji ?? "\u{1F3AE}");
  const deckLabel = session.mode === "mixed" ? "Mixed" : (deckMeta?.label ?? session.mode);

  useEffect(() => {
    setAnswerText("");
  }, [card?.cardId]);

  useEffect(() => {
    if (card?.resolution === "drawn") buzz(15);
  }, [card?.cardId, card?.resolution]);

  async function handleAnswerSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = answerText.trim();
    if (!trimmed || answering) return;
    setAnswering(true);
    await onAnswer(trimmed);
    setAnswering(false);
    buzz(20);
  }

  function handleDrawClick(): void {
    buzz(15);
    onDraw();
  }

  function handleSkipClick(): void {
    buzz(10);
    onSkip();
  }

  async function handlePlayAgainClick(): Promise<void> {
    setPlayingAgain(true);
    await onPlayAgain();
    setPlayingAgain(false);
  }

  async function handleChooseAnotherDeckClick(): Promise<void> {
    setChoosingDeck(true);
    await onChooseAnotherDeck();
    setChoosingDeck(false);
  }

  return (
    <Backdrop role="dialog" aria-modal="true" aria-label="Game">
      <Panel>
        <div className="d-flex align-items-center justify-content-between p-3 pb-2">
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <i className="bi bi-controller text-pink" aria-hidden="true" />
            <div style={{ minWidth: 0 }}>
              <div className="fw-semibold small text-truncate">
                {session.status === "pending" ? `Waiting for ${session.peerName}…` : `Playing with ${session.peerName}`}
              </div>
              {session.status !== "pending" && (
                <div className="small muted text-truncate">
                  {deckEmoji} {deckLabel}
                  {intensityMeta ? ` · ${intensityMeta.emoji} ${intensityMeta.label}` : ""}
                </div>
              )}
            </div>
          </div>
          <IconButton aria-label="Leave game" size="small" onClick={onLeave} disabled={leaving}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </IconButton>
        </div>

        <div className="px-3 pb-3 d-flex flex-column gap-3">
          {session.status === "pending" && (
            <div className="text-center py-4">
              <div className="spinner-border text-pink mb-2" role="status" style={{ width: 32, height: 32 }} />
              <div className="small muted">Waiting for {session.peerName} to join…</div>
            </div>
          )}

          {session.status === "finished" && (
            <div className="text-center py-3">
              <div style={{ fontSize: "2.5rem" }}>{"\u{1F389}"}</div>
              <div className="fw-semibold mb-1" style={{ fontSize: "1.1rem" }}>
                Game Complete
              </div>
              <div className="small muted mb-3">
                You played <strong>{session.cardsPlayed}</strong> cards together
                {session.cardsSkipped > 0 ? ` (${session.cardsSkipped} skipped)` : ""}.
              </div>
              <div className="d-flex flex-column gap-2">
                <Button variant="contained" color="primary" onClick={handlePlayAgainClick} disabled={playingAgain || choosingDeck}>
                  <i className="bi bi-arrow-repeat me-2" aria-hidden="true" style={{ marginRight: 8 }} />
                  {playingAgain ? "Starting…" : "Play Again"}
                </Button>
                <Button variant="outlined" onClick={handleChooseAnotherDeckClick} disabled={playingAgain || choosingDeck}>
                  {choosingDeck ? "Loading…" : "Choose Another Deck"}
                </Button>
                <Button onClick={onLeave} disabled={leaving || playingAgain || choosingDeck}>
                  Back to Chat
                </Button>
              </div>
            </div>
          )}

          {session.status === "active" && (
            <>
              {/* Explicit, always-visible turn state - not just implied by
                  which buttons happen to be enabled. */}
              <div className="d-flex align-items-center justify-content-between">
                <TurnPill $mine={session.isMyTurn}>{session.isMyTurn ? "Your turn" : `${session.peerName}'s turn`}</TurnPill>
                <span className="small muted">
                  {session.cardsPlayed} / {session.deckSize} cards
                </span>
              </div>

              <CardBody key={cardKey}>
                {!card && (
                  <div className="text-center py-4">
                    <div className="small muted mb-3">
                      {session.isMyTurn ? "It's your turn to draw a card." : `Waiting for ${session.peerName} to draw…`}
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleDrawClick}
                      disabled={!session.isMyTurn || drawing}
                      aria-label="Draw card"
                    >
                      <i className="bi bi-shuffle me-2" aria-hidden="true" style={{ marginRight: 8 }} />
                      {drawing ? "Drawing…" : "Draw Card"}
                    </Button>
                  </div>
                )}

                {card && (
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="small fw-semibold text-uppercase" style={{ color: "var(--pink-600)", letterSpacing: "0.03em" }}>
                        {CATEGORY_EMOJI[card.deckId] || "\u{1F3AE}"} {card.category}
                      </span>
                      <span className="small muted">Card {session.cardsPlayed + 1}</span>
                    </div>

                    <div
                      className="p-3 mb-3"
                      style={{
                        background: "var(--surface-soft)",
                        border: "1px solid var(--surface-border)",
                        borderRadius: "var(--radius-sm, 12px)",
                        boxShadow: "var(--shadow-sm, 0 2px 8px rgba(0,0,0,.08))",
                        fontSize: "1.2rem",
                        fontWeight: 500,
                        opacity: card.resolution === "drawn" ? 1 : 0.75,
                      }}
                    >
                      {card.text}
                      {card.resolution === "skipped" && <div className="small muted mt-2">Skipped</div>}
                    </div>

                    {lastAnswer && card.resolution === "answered" && (
                      <div className="mb-3">
                        <div className="small fw-semibold mb-1">{lastAnswer.mine ? "You answered" : `${session.peerName} answered`}</div>
                        <div
                          className="p-2"
                          style={{
                            background: lastAnswer.mine ? "var(--pink-500)" : "var(--surface-strong)",
                            color: lastAnswer.mine ? "#fff" : "inherit",
                            borderRadius: "var(--radius-sm, 12px)",
                          }}
                        >
                          {lastAnswer.text}
                        </div>
                      </div>
                    )}

                    {card.resolution === "drawn" && (
                      <>
                        <form onSubmit={handleAnswerSubmit} className="d-flex align-items-center gap-1 mb-2">
                          <TextField
                            value={answerText}
                            onChange={(e) => setAnswerText(e.target.value)}
                            placeholder="Type your answer…"
                            size="small"
                            fullWidth
                            disabled={answering}
                          />
                          <IconButton type="submit" aria-label="Send answer" size="small" disabled={!answerText.trim() || answering}>
                            <i className="bi bi-send-fill text-pink" aria-hidden="true" />
                          </IconButton>
                        </form>
                        {session.isMyTurn && (
                          <Button size="small" onClick={handleSkipClick} disabled={skipping} aria-label="Skip card">
                            {skipping ? "Skipping…" : "Skip"}
                          </Button>
                        )}
                      </>
                    )}

                    {card.resolution !== "drawn" && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDrawClick}
                        disabled={!session.isMyTurn || drawing}
                        fullWidth
                        aria-label="Next card"
                      >
                        <i className="bi bi-arrow-right-circle me-2" aria-hidden="true" style={{ marginRight: 8 }} />
                        {drawing ? "Drawing…" : session.isMyTurn ? "Next Card" : `Waiting for ${session.peerName}…`}
                      </Button>
                    )}
                  </div>
                )}
              </CardBody>
            </>
          )}
        </div>
      </Panel>
    </Backdrop>
  );
}
